import { useCallback, useMemo, useRef, useState } from 'react';

import { useLazyGetInstagramPostsQuery } from '@app/api/SocialIntegrations';
import type { InstagramPostsPageDto } from '@app/api/SocialIntegrations';
import {
  INSTAGRAM_PUBLICATIONS_LOAD_MORE_OFFSET_PX,
  INSTAGRAM_PUBLICATIONS_PAGE_SIZE,
  MAX_SELECTED_INSTAGRAM_PUBLICATIONS,
} from '@app/components/Modals/BotDetailsModal/constants/instagramPublications';
import type { InstagramPublication } from '@app/components/Modals/BotDetailsModal/types';
import {
  countUnloadedSelectedPublications,
  getPostsRefreshNote,
  mapInstagramPost,
  mergeInstagramPublications,
  removeUnloadedSelectedPublications,
  toggleInstagramPublication,
} from '@app/components/Modals/BotDetailsModal/utils/instagramPublications';
import { useUserPrint } from '@app/hooks';
import { handleCatchError } from '@app/services/__global__/handleCatchError';

import type { UIEvent } from 'react';

export interface UseInstagramPublicationsGridProps {
  value?: string[];
  onChange?: (instagramPublicationIds: string[]) => void;
  integrationId: number;
  isEnabled: boolean;
}

export const useInstagramPublicationsGrid = ({
  value,
  onChange,
  integrationId,
  isEnabled,
}: UseInstagramPublicationsGridProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const [getInstagramPosts] = useLazyGetInstagramPostsQuery();

  const [publications, setPublications] = useState<InstagramPublication[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [postsRefreshNote, setPostsRefreshNote] = useState<string | null>(null);
  const [loadedIntegrationId, setLoadedIntegrationId] = useState<number | null>(
    null
  );
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Текст ошибки из ErrorMessage ответа CRM или null
  const [firstPageError, setFirstPageError] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // Номер последнего запроса первой страницы: ответ на старую интеграцию
  // или на старое обновление списка не должен перезаписать новый
  const requestIdRef = useRef(0);
  // Прокрутка шлёт много событий раньше, чем React обновит состояние.
  // Поэтому замок, смещение, признак следующей страницы и ошибка — в ref:
  // следующее событие читает их сразу, без перерисовки.
  const isLoadingMoreRef = useRef(false);
  // Смещение следующей страницы — сколько публикаций отдал сервер,
  // а не сколько осталось после склейки без повторов
  const nextOffsetRef = useRef(0);
  const hasMoreRef = useRef(false);
  const hasLoadMoreErrorRef = useRef(false);

  const selectedIds = useMemo(() => value ?? [], [value]);
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length;
  const isLimitReached = selectedCount >= MAX_SELECTED_INSTAGRAM_PUBLICATIONS;
  const isLoadedForIntegration = loadedIntegrationId === integrationId;

  const unloadedSelectedCount = useMemo(
    () => countUnloadedSelectedPublications(selectedIds, publications),
    [selectedIds, publications]
  );

  const requestPage = useCallback(
    (offset: number): Promise<InstagramPostsPageDto> =>
      getInstagramPosts({
        ActiveBranchId,
        integrationId,
        limit: INSTAGRAM_PUBLICATIONS_PAGE_SIZE,
        offset,
      }).unwrap(),
    [ActiveBranchId, getInstagramPosts, integrationId]
  );

  const applyPageMeta = useCallback((page: InstagramPostsPageDto) => {
    // HasMore с пустой страницей — конец списка: иначе запросы пойдут по кругу
    const hasNextPage = page.HasMore && page.Items.length > 0;

    hasMoreRef.current = hasNextPage;
    setHasMore(hasNextPage);
    setPostsRefreshNote(getPostsRefreshNote(page.PostsRefreshedAt, Date.now()));
  }, []);

  const resetLoadMore = useCallback(() => {
    isLoadingMoreRef.current = false;
    hasLoadMoreErrorRef.current = false;
    setIsLoadingMore(false);
    setLoadMoreError(null);
  }, []);

  // Первая страница. Шлюз на каждый запрос ставит фоновое обновление
  // списка, поэтому ручное обновление — тот же запрос первой страницы.
  const loadFirstPage = useCallback(
    async (isManualRefresh: boolean) => {
      if (!ActiveBranchId) return;

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const isCurrentRequest = () => requestId === requestIdRef.current;

      resetLoadMore();
      setFirstPageError(null);
      setLoadedIntegrationId(integrationId);

      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        hasMoreRef.current = false;
        setPublications([]);
        setHasMore(false);
        setPostsRefreshNote(null);
        setIsFirstPageLoading(true);
      }

      try {
        const page = await requestPage(0);

        if (!isCurrentRequest()) return;

        nextOffsetRef.current = page.Items.length;
        setPublications(
          mergeInstagramPublications([], page.Items.map(mapInstagramPost))
        );
        applyPageMeta(page);
      } catch (error) {
        // Ошибку показывает сетка: при пустом списке — вместо сетки,
        // при загруженном — строкой у кнопки обновления
        if (isCurrentRequest()) setFirstPageError(handleCatchError(error));
      } finally {
        if (isCurrentRequest()) {
          setIsFirstPageLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [ActiveBranchId, applyPageMeta, integrationId, requestPage, resetLoadMore]
  );

  // isRetry — запрос по кнопке «Повторить». После ошибки прокрутка
  // и «Показать ещё» новую страницу не запрашивают.
  const loadNextPage = useCallback(
    async (isRetry: boolean) => {
      if (
        !hasMoreRef.current ||
        isLoadingMoreRef.current ||
        (hasLoadMoreErrorRef.current && !isRetry) ||
        isFirstPageLoading ||
        isRefreshing
      ) {
        return;
      }

      const requestId = requestIdRef.current;
      const isCurrentRequest = () => requestId === requestIdRef.current;

      isLoadingMoreRef.current = true;
      hasLoadMoreErrorRef.current = false;
      setIsLoadingMore(true);
      setLoadMoreError(null);

      try {
        const page = await requestPage(nextOffsetRef.current);

        if (!isCurrentRequest()) return;

        // Смещение и признак следующей страницы обновляются до снятия
        // замка в finally: следующее событие прокрутки увидит новые значения
        nextOffsetRef.current += page.Items.length;
        setPublications((loaded) =>
          mergeInstagramPublications(loaded, page.Items.map(mapInstagramPost))
        );
        applyPageMeta(page);
      } catch (error) {
        // Ошибку показывает сетка строкой с кнопкой «Повторить»
        if (isCurrentRequest()) {
          hasLoadMoreErrorRef.current = true;
          setLoadMoreError(handleCatchError(error));
        }
      } finally {
        if (isCurrentRequest()) {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        }
      }
    },
    [applyPageMeta, isFirstPageLoading, isRefreshing, requestPage]
  );

  const refreshHandler = useCallback(() => {
    loadFirstPage(publications.length > 0);
  }, [loadFirstPage, publications.length]);

  const loadNextPageHandler = useCallback(() => {
    loadNextPage(false);
  }, [loadNextPage]);

  const retryNextPageHandler = useCallback(() => {
    loadNextPage(true);
  }, [loadNextPage]);

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

      if (
        scrollHeight - scrollTop - clientHeight <=
        INSTAGRAM_PUBLICATIONS_LOAD_MORE_OFFSET_PX
      ) {
        loadNextPage(false);
      }
    },
    [loadNextPage]
  );

  const togglePublicationHandler = useCallback(
    (instagramId: string) => {
      onChange?.(toggleInstagramPublication(selectedIds, instagramId));
    },
    [onChange, selectedIds]
  );

  const clearSelectionHandler = useCallback(() => {
    onChange?.([]);
  }, [onChange]);

  const removeUnloadedSelectionHandler = useCallback(() => {
    onChange?.(removeUnloadedSelectedPublications(selectedIds, publications));
  }, [onChange, publications, selectedIds]);

  // Список загружен без ошибки: только тогда «нет в списке» — правда
  const isListReady =
    isLoadedForIntegration && !isFirstPageLoading && firstPageError === null;
  // Пустой список при успешном ответе — «публикаций нет», а не ошибка
  const isEmpty = isListReady && publications.length === 0;
  const isFirstPageFailed =
    firstPageError !== null && publications.length === 0;
  const isRefreshFailed = firstPageError !== null && publications.length > 0;
  // Выбранные ниже по списку: страницы ещё есть
  const unloadedBelowCount = hasMore ? unloadedSelectedCount : 0;
  // Выбранных нет во всём списке: страниц больше нет
  const missingSelectedCount =
    isListReady && !hasMore ? unloadedSelectedCount : 0;

  return {
    isEnabled,
    isLoadedForIntegration,
    loadFirstPage,
    publications,
    selectedIdsSet,
    selectedCount,
    isLimitReached,
    unloadedBelowCount,
    missingSelectedCount,
    postsRefreshNote,
    hasMore,
    isFirstPageLoading,
    isRefreshing,
    isLoadingMore,
    firstPageError,
    loadMoreError,
    isEmpty,
    isFirstPageFailed,
    isRefreshFailed,
    refreshHandler,
    loadNextPageHandler,
    retryNextPageHandler,
    scrollHandler,
    togglePublicationHandler,
    clearSelectionHandler,
    removeUnloadedSelectionHandler,
  };
};
